import * as React from "react";
import { I18n } from "react-redux-i18n";
import { Modal, ModalBody, ModalHeader, ModalTitle } from '@common/components/modal';

import { getTestAttributes } from "@common/utils/tests";
import { Flex } from "@common/components/Flex";
import { Button } from "@common/components/Button";
import { FolderListDto } from "@common/dto/folder/list-dto";
import { IndividualListDto } from "@common/dto/individual/list-dto";
import { PedigreeGetDto } from "@common/dto/pedigree/get-dto";

import { useController } from "./move-to-pedigree.controller";
import { MoveToNewPedigree } from "./components/move-to-new-pedigree";
import { MoveToExistingPedigree } from "./components/move-to-existing-pedigree";

import "./move-to-pedigree.styles.scss";

type MoveToPedigreeProps = {
  onClose: () => void;
  onSubmit: () => void;
  showErrorMessage: (message?: string) => void;
  pedigreeExists: boolean;
  folders: FolderListDto[];
  individual: IndividualListDto;
  moveIndividualToNewPedigree: (payload: {
    folderId?: FolderListDto["id"];
    familyName: string;
    familyId: IndividualListDto['familyId'];
    individualName: string;
  }) => Promise<void>;
  moveIndividualToExistingPedigree: (payload: {
    newFamilyId?: PedigreeGetDto["id"];
    oldFamilyId: IndividualListDto['familyId'];
  }) => Promise<void>;
};

export const MoveToPedigree = ({
  onClose,
  onSubmit,
  pedigreeExists,
  showErrorMessage,
  folders,
  individual,
  moveIndividualToNewPedigree,
  moveIndividualToExistingPedigree
}: MoveToPedigreeProps) => {
  const {
    disableSubmitAction,
    handleSubmit,
    folderId,
    pedigreeName,
    setFolderId,
    setPedigreeName,
    handlePedigreeSelect,
    selectedPedigree,
  } = useController({
    moveIndividualToNewPedigree,
    moveIndividualToExistingPedigree,
    showErrorMessage,
    pedigreeExists,
    onSubmit,
    individual
  });

  return (
    <Modal
      show
      bsSize="medium"
      bsClass="MoveToPedigree bootstrap modal"
      onHide={onClose}
      onEscapeKeyDown={onClose}
      animation={false}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle {...getTestAttributes({ id: "TABLE_MoveToPedWnd_1002" })}>
          {I18n.t("Move Individuals to Pedigree")}
        </ModalTitle>
      </ModalHeader>
      <ModalBody
        className="MoveToPedigree__body"
        {...getTestAttributes({ id: "ind-move-new-ped-wnd-content" })}
      >
        <Flex>
          <div className="MoveToPedigree__form">
            {pedigreeExists ? (
              <MoveToExistingPedigree
                showErrorMessage={showErrorMessage}
                selectedPedigree={selectedPedigree}
                onPedigreeSelect={handlePedigreeSelect}
              />
            ) : (
              <MoveToNewPedigree
                folders={folders}
                pedigreeName={pedigreeName}
                folderId={folderId}
                onFolderIdChange={setFolderId}
                onPedigreeNameChange={setPedigreeName}
              />
            )}
            <div
              className="MoveToPedigree__individual-table"
              {...getTestAttributes({ id: "ind-move-new-ped-wnd-list" })}
            >
              <div className="MoveToPedigree__individual-table__header">
                <span>{I18n.t("Individuals")}</span>
              </div>
              <Flex
                alignItems="center"
                className="MoveToPedigree__individual-table__body"
              >
                <img src={individual.img} />
                <span>{individual.name}</span>
              </Flex>
            </div>
          </div>
          <Flex
            justify="flex-end"
            alignItems="flex-start"
            className="MoveToPedigree__form__control"
          >
            <Button
              className="MoveToPedigree__form__submit-btn"
              bsStyle="primary"
              onClick={handleSubmit}
              disabled={disableSubmitAction}
              {...getTestAttributes({ id: "ind-move-new-ped-wnd-save-btn" })}
            >
              {I18n.t("Save")}
            </Button>
          </Flex>
        </Flex>
      </ModalBody>
    </Modal>
  );
};
